(() => {
  const BUTTON_SELECTOR = "[data-wk-custom-button]";
  const LOADING_CLASS = "is-loading";
  const SELECTED_CLASS = "is-in-wishlist";
  const GLOBAL_LOADING_CLASS = "wishlist-loading";
  const WISHLIST_PRODUCT_CARD_SELECTOR = ".wishlist-page-content wishlist-product-card";
  let wishlist = null;
  let wishlistReady = false;
  let app = null;
  let pendingRequests = 0;
  let wishlistNotifyScanTimer = null;
  const productJsonCache = new Map();

  const toInt = (value) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getHandle = (button) => button.dataset.wkProductHandle || "";

  const getVariantId = (button) => {
    if (button.dataset.wkDynamicVariant === "true") {
      const productRoot = button.closest(".product") || document;
      const variantInput =
        productRoot.querySelector('input.product-variant-id[name="id"]:not([disabled])') ||
        productRoot.querySelector('form[action*="/cart/add"] input[name="id"]:not([disabled])') ||
        productRoot.querySelector('form[action*="/cart/add"] input[name="id"]');
      if (variantInput && variantInput.value) return toInt(variantInput.value);
    }
    return toInt(button.dataset.wkVariantId);
  };

  const getItemForButton = (button) => {
    if (!wishlist || !Array.isArray(wishlist.items)) return null;

    const knownItemId = button.dataset.wkWishlistItemId;
    if (knownItemId) {
      const knownItem = wishlist.items.find((item) => String(item?.id) === String(knownItemId));
      if (knownItem) return knownItem;
    }

    const handle = getHandle(button);
    if (!handle) return null;

    const variantId = getVariantId(button);
    const wishlistMode = String(app?.settings?.general?.wishlistMode || "").toUpperCase();
    const variantMode = wishlistMode.includes("VARIANT");

    return (
      wishlist.items.find((item) => {
        if (!item?.product?.handle || item.product.handle !== handle) return false;
        if (!variantMode) return true;
        return toInt(item.variantId) === variantId;
      }) || null
    );
  };

  const applyButtonState = (button) => {
    const item = getItemForButton(button);
    const inWishlist = Boolean(item);
    button.classList.toggle(SELECTED_CLASS, inWishlist);
    button.setAttribute("aria-pressed", inWishlist ? "true" : "false");
    if (item?.id) {
      button.dataset.wkWishlistItemId = String(item.id);
    } else {
      delete button.dataset.wkWishlistItemId;
    }
  };

  const applyAllStates = () => {
    document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => applyButtonState(button));
  };

  const getGlobalLoader = () => document.getElementById("wishlist-global-loader");

  const setGlobalLoading = (loading) => {
    pendingRequests = loading ? pendingRequests + 1 : Math.max(0, pendingRequests - 1);
    const active = pendingRequests > 0;
    const globalLoader = getGlobalLoader();

    if (globalLoader) {
      globalLoader.classList.toggle("hidden", !active);
      globalLoader.setAttribute("aria-hidden", active ? "false" : "true");
    }

    document.documentElement.classList.toggle(GLOBAL_LOADING_CLASS, active);
  };

  const setLoading = (button, loading) => {
    button.disabled = loading;
    button.classList.toggle(LOADING_CLASS, loading);
    button.setAttribute("aria-busy", loading ? "true" : "false");
    setGlobalLoading(loading);
  };

  const getWishlistNotifyLabel = () => {
    const root = document.querySelector(".wishlist-page-content");
    if (root?.dataset?.wishlistNotifyLabel) return root.dataset.wishlistNotifyLabel;
    const lang = document.documentElement.lang || "";
    return lang.toLowerCase().startsWith("en") ? "Notify me" : "Avvisami";
  };

  const getWishlistProductLink = (card) =>
    card.querySelector('.wk-image-link[href*="/products/"], .wk-text-link[href*="/products/"]');

  const getProductJsonPath = (card) => {
    const link = getWishlistProductLink(card);
    if (!link) return "";

    const url = new URL(link.getAttribute("href"), window.location.origin);
    if (!url.pathname.includes("/products/")) return "";

    return `${url.pathname.replace(/\/$/, "")}.js`;
  };

  const getWishlistVariantId = (card) => {
    const variantInput = card.querySelector('.wk-form input[name="id"]');
    if (variantInput?.value) return toInt(variantInput.value);

    const link = getWishlistProductLink(card);
    if (!link) return null;

    const url = new URL(link.getAttribute("href"), window.location.origin);
    return toInt(url.searchParams.get("variant"));
  };

  const fetchProductJson = (jsonPath) => {
    if (!jsonPath) return Promise.resolve(null);
    if (!productJsonCache.has(jsonPath)) {
      productJsonCache.set(
        jsonPath,
        fetch(jsonPath, { headers: { Accept: "application/json" } })
          .then((response) => (response.ok ? response.json() : null))
          .catch((error) => {
            console.warn("[wishlist-custom] Product availability fetch failed", jsonPath, error);
            return null;
          })
      );
    }
    return productJsonCache.get(jsonPath);
  };

  const bindWishlistNotifyForm = (form) => {
    if (!form || form.dataset.wishlistNotifySubmitBound === "true") return;
    form.dataset.wishlistNotifySubmitBound = "true";
    form.addEventListener("submit", (event) => {
      if (form.dataset.wishlistNotifyActive !== "true") return;
      event.preventDefault();
      event.stopImmediatePropagation();
    });
  };

  const saveWishlistButtonOriginalState = (button) => {
    if (button.dataset.wishlistOriginalSaved === "true") return;
    button.dataset.wishlistOriginalSaved = "true";
    button.dataset.wishlistOriginalHtml = button.innerHTML;
    button.dataset.wishlistOriginalClass = button.className;
    button.dataset.wishlistOriginalType = button.getAttribute("type") || "";
    button.dataset.wishlistOriginalDisabled = button.disabled ? "true" : "false";
    button.dataset.wishlistOriginalAriaDisabled = button.getAttribute("aria-disabled") || "";
  };

  const restoreWishlistButtonOriginalState = (button) => {
    if (button.dataset.wishlistOriginalSaved !== "true") return;

    button.innerHTML = button.dataset.wishlistOriginalHtml || button.innerHTML;
    button.className = button.dataset.wishlistOriginalClass || button.className;

    if (button.dataset.wishlistOriginalType) {
      button.setAttribute("type", button.dataset.wishlistOriginalType);
    } else {
      button.removeAttribute("type");
    }

    button.disabled = button.dataset.wishlistOriginalDisabled === "true";

    if (button.dataset.wishlistOriginalAriaDisabled) {
      button.setAttribute("aria-disabled", button.dataset.wishlistOriginalAriaDisabled);
    } else {
      button.removeAttribute("aria-disabled");
    }

    delete button.dataset.productId;
    delete button.dataset.customerId;
    delete button.dataset.customerEmail;
  };

  const setWishlistNotifyState = (card, product, unavailable, processKey) => {
    const form = card.querySelector(".wk-form");
    const button = form?.querySelector(".wk-cta-button");
    if (!form || !button) return;

    bindWishlistNotifyForm(form);
    form.dataset.wishlistNotifyProcessKey = processKey;

    if (!unavailable) {
      form.dataset.wishlistNotifyActive = "false";
      restoreWishlistButtonOriginalState(button);
      return;
    }

    saveWishlistButtonOriginalState(button);
    form.dataset.wishlistNotifyActive = "true";

    button.setAttribute("type", "button");
    button.disabled = false;
    button.removeAttribute("aria-disabled");
    button.classList.add("open-button-modal", "wk-cta-button--notify");
    button.setAttribute("value", "Notifica");
    button.dataset.productId = String(product.id);
    button.dataset.customerId = "0";

    const label = button.querySelector(".wk-cta-label");
    if (label) {
      label.textContent = getWishlistNotifyLabel();
    } else {
      button.textContent = getWishlistNotifyLabel();
    }
  };

  const isWishlistNotifyApplied = (form, button) =>
    form?.dataset?.wishlistNotifyActive === "true" &&
    button?.classList.contains("open-button-modal") &&
    button?.classList.contains("wk-cta-button--notify") &&
    button?.disabled === false &&
    Boolean(button?.dataset?.productId);

  const processWishlistProductCard = async (card) => {
    if (!(card instanceof Element)) return;
    if (!card.querySelector(".wk-form .wk-cta-button")) return;

    const variantId = getWishlistVariantId(card);
    const jsonPath = getProductJsonPath(card);
    const processKey = `${jsonPath}:${variantId || ""}`;
    const form = card.querySelector(".wk-form");
    const button = form?.querySelector(".wk-cta-button");
    const alreadyProcessed =
      card.dataset.wishlistNotifyProcessKey === processKey &&
      form?.dataset.wishlistNotifyProcessKey === processKey &&
      (form.dataset.wishlistNotifyActive !== "true" || isWishlistNotifyApplied(form, button));

    if (!jsonPath || !variantId || alreadyProcessed) return;
    card.dataset.wishlistNotifyProcessKey = processKey;

    const product = await fetchProductJson(jsonPath);
    if (!product || !Array.isArray(product.variants) || !card.isConnected) {
      delete card.dataset.wishlistNotifyProcessKey;
      return;
    }

    const currentVariantId = getWishlistVariantId(card);
    if (currentVariantId !== variantId) {
      delete card.dataset.wishlistNotifyProcessKey;
      scheduleWishlistNotifyScan();
      return;
    }

    const variant = product.variants.find((item) => toInt(item.id) === variantId);
    if (!variant) {
      delete card.dataset.wishlistNotifyProcessKey;
      return;
    }

    setWishlistNotifyState(card, product, variant.available === false, processKey);
  };

  const processWishlistNotifyCards = () => {
    document
      .querySelectorAll(WISHLIST_PRODUCT_CARD_SELECTOR)
      .forEach((card) => processWishlistProductCard(card));
  };

  const scheduleWishlistNotifyScan = () => {
    window.clearTimeout(wishlistNotifyScanTimer);
    wishlistNotifyScanTimer = window.setTimeout(processWishlistNotifyCards, 80);
  };

  const handleButtonClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!app) return;

    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.disabled) return;

    const productHandle = getHandle(button);
    const variantId = getVariantId(button);
    if (!productHandle || !variantId) return;

    setLoading(button, true);

    try {
      const existingItem = getItemForButton(button);
      if (existingItem?.id != null) {
        try {
          await app.removeWishlistItem({ wishlistItemId: existingItem.id });
        } catch (removeByIdError) {
          await app.removeWishlistItem({ productHandle, variantId, wishlistId: "mine" });
        }
        if (wishlist && Array.isArray(wishlist.items)) {
          wishlist = {
            ...wishlist,
            items: wishlist.items.filter((item) => String(item?.id) !== String(existingItem.id)),
          };
        }
      } else {
        await app.addWishlistItem({ productHandle, variantId });
      }
    } catch (error) {
      console.error("Wishlist action failed", error);
    } finally {
      setLoading(button, false);
      applyButtonState(button);
    }
  };

  const bindButtons = () => {
    document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
      if (button.dataset.wkBound === "true") return;
      button.dataset.wkBound = "true";
      button.addEventListener("click", handleButtonClick);
      applyButtonState(button);
    });
    scheduleWishlistNotifyScan();
  };

  const watchWishlist = () => {
    if (!app) return;

    app.state.observeWishlist({ wishlistId: "mine" }).subscribe((nextWishlist) => {
      wishlist = nextWishlist;
      wishlistReady = true;
      applyAllStates();
    });
  };

  const boot = () => {
    app = window.WishlistKing || null;
    bindButtons();
    if (!app) return;
    watchWishlist();
  };

  const observeDom = () => {
    const observer = new MutationObserver((mutations) => {
      let shouldRebind = false;
      let shouldScanWishlistNotify = false;
      for (const mutation of mutations) {
        if (mutation.type === "attributes" && mutation.target instanceof Element) {
          if (
            mutation.target.matches('.wk-form input[name="id"]') ||
            mutation.target.matches(".wk-cta-button") ||
            mutation.target.closest?.(WISHLIST_PRODUCT_CARD_SELECTOR)
          ) {
            shouldScanWishlistNotify = true;
          }
        }
        if (mutation.type === "childList" && mutation.target instanceof Element) {
          if (mutation.target.closest?.(WISHLIST_PRODUCT_CARD_SELECTOR)) {
            shouldScanWishlistNotify = true;
          }
        }
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches(BUTTON_SELECTOR) || node.querySelector(BUTTON_SELECTOR)) {
            shouldRebind = true;
          }
          if (
            node.matches(WISHLIST_PRODUCT_CARD_SELECTOR) ||
            node.closest?.(WISHLIST_PRODUCT_CARD_SELECTOR) ||
            node.querySelector(WISHLIST_PRODUCT_CARD_SELECTOR)
          ) {
            shouldScanWishlistNotify = true;
          }
          if (shouldRebind && shouldScanWishlistNotify) break;
        }
        if (shouldRebind && shouldScanWishlistNotify) break;
      }
      if (shouldRebind) bindButtons();
      if (shouldScanWishlistNotify) scheduleWishlistNotifyScan();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["value", "disabled", "class", "aria-disabled"],
      childList: true,
      subtree: true,
    });
  };

  const listenWishlistEvents = () => {
    const events = [
      "wk:wishlist:add:success",
      "wk:wishlist:remove:success",
      "wk:wishlist:change-variant:success",
      "wk:wishlist:clear:success",
      "wk:customer:login",
      "wk:customer:logout",
      "wk:product:change-variant:success",
    ];

    events.forEach((eventName) => {
      document.addEventListener(eventName, () => {
        if (!wishlistReady) return;
        applyAllStates();
        scheduleWishlistNotifyScan();
      });
    });
  };

  const listenWishlistVariantChanges = () => {
    document.addEventListener("change", (event) => {
      const card = event.target?.closest?.(WISHLIST_PRODUCT_CARD_SELECTOR);
      if (!card) return;

      delete card.dataset.wishlistNotifyProcessKey;
      scheduleWishlistNotifyScan();
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("wk:app:ready", () => {
    if (!app) {
      app = window.WishlistKing || null;
      if (app) watchWishlist();
    }
    bindButtons();
  });

  observeDom();
  listenWishlistEvents();
  listenWishlistVariantChanges();
})();
